import React, { MouseEvent } from "react";
import ReactDOM from "react-dom";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as _ from "lodash";
import Cookies from 'js-cookie'
import bootbox from "bootbox";

// Import FilePond styles
import "filepond/dist/filepond.min.css";

import { UiUtils } from "../common/uiUtils";
import { ModelApi } from "../api/modelApi";
import { ViewerUtils } from "../common/viewerUtils";
import { MetadataForm } from "./metadataForm";
import * as UploadReport from "./uploadReport";

registerPlugin(FilePondPluginFileValidateType);

interface UploadProps {
    project?: any;
    modelSettingsIds: { [key: string]: string; };
    fileExtensions: string[];
    uploadChunkSize: number;
}

interface UploadItem {
    filename: string;
    serverId: string;
    queryId?: string;
    queued: boolean;
}

type FileMap = { [key: string]: UploadItem };

interface UploadState {
    items: FileMap;
    commitResponse: CommitResponse;
    processing: boolean;
}

interface CommitResponse {
    projectId: string;
    projectName: string;
    models: CommittedModel[];
}

interface CommittedModel {
    serverId: string;
    queryId: string;
    modelName: string;
    projectId: string;
    queued: boolean;
    status?: string;
}

interface ModelDetailsProps {
    commitResponse: CommitResponse;
}

interface ModelDetailsState {
    commitResponse: CommitResponse;
    processing: boolean;
}

const StatusIcon = (props) => {
    const realStatus = props.queued ? props.status : "DONE";

    switch (realStatus) {
    case "DONE":
        return <span className={"icon ion-md-checkbox"} style={{ color: "green" }}></span>;
    case "FAILED":
        return <span className={"icon ion-md-warning"} style={{ color: "red" }}></span>;
    default:
        return <span className={"icon ion-md-hourglass wait-spinner"} style={{ color: "blue", fontSize: "16px" }
}></span>;
    }
}

const statusText = (status, queued) => {
    const realStatus = queued ? status : "DONE";

    switch (realStatus) {
    case "DONE":
        return "Ready";
    case "FAILED":
        return "Failed";
    default:
        return "In progress";
    }
}

const StatusRow = (props) => {
    const url = `${ViewerUtils.baseUrl().replace("/upload", "")}/${props.queryId}`;
    return <tr>
               <td>
                   {!props.queued || props.status === "DONE" ? <a href={url} target="_blank">{url}</a> : url}
               </td>
               <td>
                   {props.modelName}
               </td>
               <td>
                   {statusText(props.status, props.queued)}
               </td>
               <td>
                   <StatusIcon status={props.status} queued={props.queued}/>
               </td>
           </tr>;
}

const ProjectDetails = (props) => {
    const projectUrl =
        `${ViewerUtils.baseUrl().replace("/upload", "")}/project/default/${props.projectId ||
            props.project.existingProjectId}`;
    return <div className="row">
               <div className="col">
                   <div key="project-title" className="row">
                       <h5>Project information</h5>
                   </div>
                   <div key="project-name" className="form-group row">
                       <label className="col-formlabel col-sm-3">Project</label>
                       <div className="col-sm-6">
                           <a href={projectUrl} target="_blank">{projectUrl}</a>
                       </div>
                   </div>
                   {props.modelName != null && <div key="model-name" className="form-group row">
                       <label className="col-formlabel col-sm-3">New revision for model</label>
                       <div className="col-sm-6">
                    {props.modelName}
                       </div>
                   </div>}
               </div>
           </div>;
}

class ModelDetails extends React.Component<ModelDetailsProps, ModelDetailsState> {

    private interval;

    constructor(props) {
        super(props);
        this.state = { commitResponse: props.commitResponse, processing: true };
    }

    componentDidMount(): void {
        if (this.props.commitResponse != null && _.some(this.props.commitResponse.models, ci => ci.queued)) {
            this.interval = setInterval(() => this.refreshStatus(), 10000);
        } else {
            this.setState({ processing: false });
        }
    }

    componentWillUnmount(): void {
        if (this.interval) {
            clearInterval(this.interval);
        }
    }

    private pendingStatus(item: CommittedModel) {
        if (item.queued) {
            return item.status !== "DONE" && item.status !== "FAILED";
        }
        return false;
    }

    private async refreshStatus() {
        const { data } = await ModelApi.checkUploadStatus({
            queryIds: this.state.commitResponse.models.filter(ci => this.pendingStatus(ci)).map(ci => ci.queryId)
        });
        const items = [...this.state.commitResponse.models];
        const withStatus = items.map(i => {
            const newStatus = _.find(data, d => d.queryId === i.queryId);
            if (newStatus != null) {
                i.status = newStatus.status;
            }
            return i;
        });

        const processing = _.some(withStatus, ws => this.pendingStatus(ws));
        const updatedResponse = { ...this.state.commitResponse, models: withStatus };
        this.setState({ commitResponse: updatedResponse, processing: processing });
        if (!processing) {
            clearInterval(this.interval);
        }
    }

    render() {
        const projectUrl =
            `${ViewerUtils.baseUrl().replace("/upload", "")}/project/default/${this.state.commitResponse.projectId}`;
        return (
            <div>
                <div className="row">
                    <div className="col">
                        <table className="table table-borderless">
                            <thead>
                            <tr>
                                <th>Model URL</th>
                                <th>Name</th>
                                <th colSpan={2}>Conversion status</th>
                            </tr>
                            </thead>
                            <tbody>
                            {this.state.commitResponse != null &&
                                this.state.commitResponse.models.map(ci => <StatusRow key={ci.serverId} {...ci}/>)}
                            </tbody>
                        </table>
                        <button className="btn btn-primary mt-1" type="button" disabled={this.state.processing
} onClick={() => UploadReport.createPdf(projectUrl,
    this.state.commitResponse.projectName,
    this.state.commitResponse.models)}>
                            <span className="icon ion-md-download mr-2"></span>PDF
                        </button>
                    </div>
                </div>
            </div>
        );
    }

}

class App extends React.Component<UploadProps, UploadState> {

    private pond: any;
    private modelSettingsIds: { label: string, value: string }[];

    constructor(props) {
        super(props);

        this.state = {
            items: {},
            commitResponse: null,
            processing: false,
        };
        this.modelSettingsIds = [{ label: "None", value: "" }];
        this.modelSettingsIds = [
            ...this.modelSettingsIds, ...Object.keys(props.modelSettingsIds).map(k => ({
                label: k,
                value:
                    props.modelSettingsIds[k]
            }))
        ];
    }

    private onProcessFile = (err, file) => {
        if (!err) {
            const items = { ...this.state.items };
            items[file.serverId] = { filename: file.filename, serverId: file.serverId, queued: false };
            this.setState({ items: items });
        }
    }

    private onProcessFiles = () => {
        this.setState({ processing: false });
    }

    private onRemoveFile = (err, file) => {
        if (!err) {
            //TODO https://github.com/pqina/react-filepond/issues/26
            const remaining = this.pond.getFiles();
            const items = {};
            for (let f of remaining) {
                items[f.serverId] = { filename: f.filename, serverId: f.serverId, queued: false };
            }

            this.setState({ items: items });
        }
    }

    render() {
        return (
            <div className="container">
                <div className="row">
                    <div className="col-md">
                        <FilePond
                            allowFileTypeValidation={true}
                            acceptedFileTypes={this.props.fileExtensions}
                            fileValidateTypeDetectType={(source, type) => new Promise((resolve, reject) => {
                                resolve(_.last(source.name.split(".")));
                            })}

                            ref={ref => (this.pond = ref)}
                            allowMultiple={_.get(this.props, "project.revisionModel") == null}
                            allowReplace={false}
                            allowRevert={true}
                            maxFiles={10}
                            chunkUploads={true}
                            chunkSize={this.props.uploadChunkSize * 1024 * 1024}
                            server={{
                                url: "/sd",
                                process: {
                                    url: "/api/upload/uploadFile",
                                    onload: (resp) => {
                                        if (typeof resp === "string")
                                            return resp;
                                        else if (typeof resp === "object" && resp.response !== "") {
                                            return resp.response.concat(`.${this.pond.getFile().fileExtension}`);
                                        }
                                        else if (typeof resp === "object" && resp.getResponseHeader("upload-offset"))
                                            return resp.getResponseHeader("upload-offset");

                                        return resp;
                                    },
                                    headers: {
                                        "X-XSRF-TOKEN": Cookies.get("XSRF-TOKEN")
                                    },
                                    onerror: (resp) => {
                                        if (resp === "UNSUPPORTED_FILE_TYPE") {
                                            bootbox.alert("Upload failed. The file type is not supported.");
                                        }
                                    }
                                },
                                revert: async (fileId, load, error) => {
                                    await ModelApi.revertUploadedModels([fileId]);
                                    load();
                                },
                                patch: {
                                    url: "/api/upload/uploadChunks/",
                                    onerror: (resp) => {
                                        if (resp === "UNSUPPORTED_FILE_TYPE") {
                                            bootbox.alert("Upload failed. The file type is not supported.");
                                        }
                                    },
                                    headers: {
                                        "X-XSRF-TOKEN": Cookies.get("XSRF-TOKEN")
                                    }
                                },
                            }}
                            onprocessfilestart={() => this.setState({ processing: true, commitResponse: null })}
                            onprocessfile={this.onProcessFile}
                            onprocessfiles={this.onProcessFiles}
                            onremovefile={this.onRemoveFile}/>
                    </div>
                </div>
                {!_.isEmpty(this.state.items) &&
                    <MetadataForm
                        projectModel={this.props.project}
                        modelSettingsIds={this.modelSettingsIds}
                        items={this.state.items}
                        processing={this.state.processing}
                        finalizeUpload={resp => {
                            this.pond.removeFiles();
                            this.setState({ items: {}, commitResponse: resp });
                        }}
                        cancelUpload={async () => {
                            await ModelApi.revertUploadedModels(_.values(this.state.items).map(i => i.serverId));
                            this.pond.removeFiles();
                            this.setState({
                                items: {},
                                commitResponse: null,
                                processing: false
                            });
                        }}/>}
                {(!_.isEmpty(this.props.project) && _.isEmpty(this.state.items) || !_.isEmpty(this.state.commitResponse)) &&
                    <ProjectDetails projectId={_.get(this.props, "project.project.id") || this.state.commitResponse.projectId}
                    modelName={_.get(this.props, "project.revisionModel.name")}/>}
                {!_.isEmpty(this.state.commitResponse) &&
                    <ModelDetails commitResponse={this.state.commitResponse}/>}
            </div>
        );
    }
}

const startUploader = (project, modelSettingsIds, fileExtensions, uploadChunkSize) => {
    ReactDOM.render(<App project={project} modelSettingsIds={modelSettingsIds} fileExtensions={fileExtensions} uploadChunkSize={uploadChunkSize} />,
        document.getElementById("upload"));

    window.addEventListener("dragover",
        e => {
            e.preventDefault();
        },
        false);
    window.addEventListener("drop",
        e => {
            e.preventDefault();
        },
        false);
}

export { startUploader, FileMap, UploadItem, CommitResponse }